// ============================================================================
// COMPONENTE: PortalCaso.jsx — v4.0 — Visão Unificada do Caso
// Padrão: Filevine / MyCase — case-centric single-page view
// ESTRUTURA: [Caderno Container (nome real)] > [ClientDriveView] + Timeline
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, CheckCircle, ChevronDown, Clock, Download, ExternalLink, FileText, FolderOpen, Info, Loader2, Upload } from 'lucide-react';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';

import { usePortalContext } from '../../context/PortalContext';
import { db } from '../../shared/api/firebase/config';
import {
  watchClientWorkspaces,
  watchClientFormalDocuments,
  updateDocumentSignature,
  updateWorkspaceSignedDrive,
  addClientEventNote,
  watchClientTimeline,
} from '../../services/PortalCaseRepository.js';
import { FIREBASE_CONFIG } from '../../config/Config';
import { FS } from '../../shared/constants/firestorePaths';
import useCurrentUser from '../../hooks/useCurrentUser';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import ClientDriveView from '../../components/portal/ClientDriveView';
import CaseProgressBar from '../../components/portal/CaseProgressBar';
import CaseNextSteps from '../../components/portal/CaseNextSteps';
import PortalAprovacoes from '../../components/portal/PortalAprovacoes';
import PortalNPS from '../../components/portal/PortalNPS';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { toTitleCasePt } from '../../utils/formatters';

const appId = FIREBASE_CONFIG.projectId;
const storage = getStorage();

const getBackendBaseUrl = () => {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

// ── Helpers ──────────────────────────────────────────────────────────────────

// Nome real do caderno: alias (editável pelo master) → processType → name → pieceType → fallback
const resolveCadernoName = (ws) =>
  String(ws?.alias || '').trim() ||
  String(ws?.processType || '').trim() ||
  String(ws?.name  || '').trim() ||
  String(ws?.pieceType || '').trim() ||
  'Caderno de Autos';

const formatEventDate = (raw) => {
  try {
    const d = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const resolveEventLabel = (row) => {
  const type = String(row?.type || '').trim().toUpperCase();
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  const text = String(payload?.text || payload?.fileName || '').trim();
  const labels = {
    PORTAL_UPLOAD_SUBMITTED:          'Arquivo enviado para análise',
    PORTAL_UPLOAD_APPROVED:           'Arquivo aprovado pelo escritório',
    PORTAL_UPLOAD_REJECTED:           'Arquivo devolvido para reenvio',
    CLIENT_DOCUMENT_VIEWED:           'Documento visualizado',
    DOCUMENT_PUBLISHED:               'Novo documento disponível',
    DOCUMENT_CONSENT_REQUESTED:       'Documento publicado com consentimento',
    CLIENT_CONSENT_ACCEPTED:          'Termos aceitos pelo cliente',
    CLIENT_CONSENT_DECLINED:          'Termos recusados pelo cliente',
    PETITION_CONSENT_ACCEPTED:        'Petição aprovada pelo cliente',
    PETITION_CONSENT_DECLINED:        'Petição recusada pelo cliente',
    DOCUMENT_SIGNATURE_UPLOADED:      'Documento assinado enviado',
    CLIENT_SIGNED_DOCUMENT_UPLOADED:  'Documento assinado enviado pelo cliente',
    USER_NOTE_ADDED:                  'Anotação adicionada pelo escritório',
    PROCESS_MOVEMENT_ADDED:           'Novo andamento processual registrado',
    PROCESS_HEARING_SCHEDULED:        'Audiência agendada',
    PROCESS_DEADLINE_ADDED:           'Prazo registrado',
    PORTAL_PRESENCE_CONFIRMED:        'Presença confirmada pelo cliente',
    CLIENT_PORTAL_SESSION_STARTED:    'Acesso ao portal',
  };
  return text || labels[type] || type.replace(/_/g, ' ').toLowerCase();
};

const HIDDEN_EVENT_TYPES = new Set([
  'CLIENT_PORTAL_SESSION_STARTED',
  'CLIENT_PORTAL_PROCESSES_VIEWED',
  'CLIENT_PORTAL_PRONTUARIO_VIEWED',
  'CLIENT_PORTAL_PROFILE_VIEWED',
  'CLIENT_PORTAL_FINANCE_VIEWED',
  'CLIENT_PORTAL_VAULT_VIEWED',
  'CLIENT_PORTAL_CASO_VIEWED',
]);

// ── Componente principal ──────────────────────────────────────────────────────

export default function PortalCaso() {
  const { getToken } = useCurrentUser();
  const { crmClient } = usePortalContext();

  const clientId = useMemo(
    () => (crmClient?.id ? String(crmClient.id) : ''),
    [crmClient?.id]
  );

  const clientName = useMemo(
    () =>
      String(crmClient?.fullName || '').trim() ||
      String(crmClient?.qualification?.physical?.socialName || '').trim() ||
      String(crmClient?.qualification?.physical?.name || '').trim() ||
      String(crmClient?.contact?.name || '').trim() ||
      'Cliente',
    [crmClient]
  );

  // ── 1. Workspaces (cadernos + metadados do caso) ─────────────────────────
  // Art.327: dupla query — clientId (cliente) + caseTeamIds (advogado vinculado)
  const [workspaceRows, setWorkspaceRows] = useState([]);
  const [wsLoading, setWsLoading] = useState(true);

  useEffect(() => {
    if (!clientId) { setWorkspaceRows([]); setWsLoading(false); return; }
    const safeAppId = String(appId || '').trim();
    if (!safeAppId) { setWsLoading(false); return; }
    setWsLoading(true);
    const unsub = watchClientWorkspaces(safeAppId, clientId, (merged) => {
      setWorkspaceRows(merged);
      setWsLoading(false);
    });
    return () => { unsub(); };
  }, [clientId]);

  // ── 2. Formal documents: CONTRACT + PROCURATION (one each per caderno) ────
  const [formalDocs, setFormalDocs] = useState([]);

  useEffect(() => {
    if (!clientId) { setFormalDocs([]); return; }
    const safeAppId = String(appId || '').trim();
    if (!safeAppId) return;
    const unsub = watchClientFormalDocuments(safeAppId, clientId, (docs) => {
      setFormalDocs(docs);
    });
    return () => { try { unsub?.(); } catch {} };
  }, [clientId]);

  // ── Upload signed document handler ─────────────────────────────────────────
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const signedInputRefs = React.useRef({});

  const handleUploadSigned = React.useCallback(async (docRecord, file, wsId) => {
    if (!file || !docRecord?.id) return;
    const safeAppId = String(appId || '').trim();
    if (!safeAppId) return;
    setUploadingDocId(docRecord.id);
    try {
      const docType = String(docRecord?.type || '').trim().toUpperCase();
      const safeClientId = String(docRecord?.clientId || clientId || '').trim();
      const folder = docType === 'PROCURATION' ? 'procurations' : docType === 'DECLARATION' ? 'declarations' : 'contracts';
      const storagePath = `artifacts/${safeAppId}/public/documents/${folder}/${safeClientId}/${Date.now()}_ASSINADO_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/pdf' });
      const signedUrl = await getDownloadURL(storageRef);

      await updateDocumentSignature(safeAppId, docRecord.id, {
        signedFileUrl: signedUrl,
        signedStoragePath: storagePath,
        signedFileName: file.name,
        signedBy: safeClientId,
        status: 'SIGNED',
      });

      // Procuração e Declaração: copiar arquivo assinado para slot no Drive (documentos processuais)
      const isDriveDoc = (docType === 'PROCURATION' || docType === 'DECLARATION') && wsId;
      if (isDriveDoc) {
        try {
          const token = await getToken(true);
          if (token) {
            const baseUrl = getBackendBaseUrl();
            const ws = workspaceRows.find((w) => w.id === wsId);
            const parentId = String(ws?.attachmentsFolderId || ws?.processPieceFolderId || ws?.driveFolderId || '').trim();
            if (parentId) {
              const prefix = docType === 'DECLARATION' ? 'DECLARACAO_ASSINADA' : 'PROCURACAO_ASSINADA';
              const driveFileName = `${prefix}_${file.name}`;
              const form = new FormData();
              form.append('file', file, driveFileName);
              form.append('parentId', parentId);
              form.append('fileName', driveFileName);
              form.append('makePublic', 'false');
              const resp = await fetch(`${baseUrl}/drive/upload-file`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: form,
              });
              if (resp.ok) {
                const driveResult = await resp.json().catch(() => ({}));
                const driveFileId = String(driveResult?.fileId || driveResult?.id || '').trim();
                const driveUrl = String(driveResult?.webViewLink || driveResult?.webContentLink || '').trim();
                if (driveFileId && safeAppId) {
                  const slotPrefix = docType === 'DECLARATION' ? 'declaracao' : 'procuracao';
                  const slotId = `${slotPrefix}-${docRecord.id}`;
                  const signedPatch = {
                    signedDriveId:   driveFileId,
                    signedDriveName: driveFileName,
                    signedDriveUrl:  driveUrl || `gdrive://${driveFileId}`,
                    signed:          true,
                  };
                  const topUpdate = {
                    [`organizerState.assignments.${slotId}.signedDriveId`]:   signedPatch.signedDriveId,
                    [`organizerState.assignments.${slotId}.signedDriveName`]: signedPatch.signedDriveName,
                    [`organizerState.assignments.${slotId}.signedDriveUrl`]:  signedPatch.signedDriveUrl,
                    [`organizerState.assignments.${slotId}.signed`]:          true,
                  };
                  await updateWorkspaceSignedDrive(safeAppId, wsId, topUpdate);
                }
              }
            }
          }
        } catch (driveErr) {
          console.warn('[RDA] Drive copy for signed document failed (non-blocking):', driveErr?.message);
        }
      }

      // Log event
      try {
        await addClientEventNote(safeAppId, safeClientId, {
          category: 'USER',
          type: 'CLIENT_SIGNED_DOCUMENT_UPLOADED',
          payload: {
            text: `Documento assinado enviado: ${docRecord?.title || docRecord?.name || 'Documento'}`,
            documentId: docRecord.id,
            documentType: docType,
            fileName: file.name,
          },
        });
      } catch {}
    } catch (err) {
      console.error('[RDA] Upload signed failed:', err);
      alert(`Falha ao enviar documento assinado: ${err?.message || 'Erro desconhecido'}`);
    } finally {
      setUploadingDocId(null);
    }
  }, [clientId, workspaceRows]);

  // ── 3. Timeline ───────────────────────────────────────────────────────────
  const [recentEvents, setRecentEvents] = useState([]);
  const [showAllTimeline, setShowAllTimeline] = useState(false);

  useEffect(() => {
    if (!clientId) { setRecentEvents([]); return; }
    const safeAppId = String(appId || '').trim();
    const unsub = watchClientTimeline(safeAppId, clientId, HIDDEN_EVENT_TYPES, (events) => {
      setRecentEvents(events);
    });
    return () => { try { unsub?.(); } catch {} };
  }, [clientId]);

  // ── NPS: detecta etapa pendente de avaliação ──────────────────────────
  const [npsWorkspace, setNpsWorkspace] = useState(null);

  useEffect(() => {
    if (wsLoading || workspaceRows.length === 0) return;
    const pending = workspaceRows.find(
      (w) => w.npsForStage && w.npsForStage !== w.npsCompletedForStage
    );
    if (!pending) { setNpsWorkspace(null); return; }
    const t = setTimeout(() => setNpsWorkspace(pending), 2000);
    return () => clearTimeout(t);
  }, [wsLoading, workspaceRows]);

  // ── 4. Log de acesso e clientName (para log) ───────────────────────────
  useEffect(() => {
    if (!clientId) return;
    logClientPortalEventOnce({
      clientId,
      type: 'CLIENT_PORTAL_CASO_VIEWED',
      actorName: clientName,
      payload: { text: 'Cliente consultou a área Meu Caso no portal.' },
      storage: 'session',
    }).catch(() => {});
  }, [clientId, clientName]);

  // ── Timeline agrupada por data (padrão GitHub/Linear) ────────────────────
  const visibleEvents = showAllTimeline ? recentEvents : recentEvents.slice(0, 6);

  const timelineGroups = useMemo(() => {
    const groups = [];
    let currentKey = null;
    for (const ev of visibleEvents) {
      const dateLabel = formatEventDate(ev.createdAt);
      if (dateLabel !== currentKey) {
        groups.push({ dateLabel, items: [] });
        currentKey = dateLabel;
      }
      groups[groups.length - 1].items.push(ev);
    }
    return groups;
  }, [visibleEvents]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {npsWorkspace && (
        <PortalNPS
          workspaceId={npsWorkspace.id}
          clientId={clientId}
          clientName={clientName}
          stage={npsWorkspace.npsForStage}
          onDone={() => setNpsWorkspace(null)}
        />
      )}
      <PortalPageHeader
        icon={<Briefcase className="w-5 h-5 text-[#C5A059]" />}
        title="Meu Caso"
        subtitle="Acompanhe os dados e os últimos acontecimentos do seu processo."
      />

      {/* ── Indicador de Progresso do Caso ────────────────────────────────── */}
      {(() => {
        const stage = workspaceRows.find(w => w.caseStage)?.caseStage || crmClient?.caseStage;
        return (
          <>
            <CaseProgressBar stage={stage} />
            <CaseNextSteps stage={stage} />
          </>
        );
      })()}

      {/* ── Cadernos de Autos: um container por workspace ────────────────── */}
      {wsLoading ? (
        <div className="border border-gray-300 rounded-lg px-3 py-2 bg-white flex items-center gap-2">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-[#C5A059]" />
          <span className="text-xs text-gray-500 portal-loading-deferred">Carregando seu caderno de autos...</span>
        </div>
      ) : workspaceRows.length === 0 ? null : (
        <div className="space-y-4">
          {workspaceRows.map((ws) => {
            const fid = String(
              ws?.processRootFolderId || ws?.matterDriveFolderId || ws?.driveFolderId || ws?.processPieceFolderId || ws?.attachmentsFolderId || ''
            ).trim();
            const cadernoName = resolveCadernoName(ws);

            // Metadados estruturados do caderno (SSOT = painel master)
            const cnj         = String(ws?.numeroCNJ || '').trim();
            const juizo       = String(ws?.juizo     || '').trim();
            const comarca     = String(ws?.comarca   || '').trim();
            const classe      = String(ws?.metadata?.classe      || '').trim();
            const assunto     = String(ws?.metadata?.assunto     || '').trim();
            const ramoDireito = String(ws?.metadata?.ramoDireito || ws?.metadata?.jurisdiction_branch || ws?.ramoDireito || '').trim();
            const partiesAll  = Array.isArray(ws?.partiesRefs) ? ws.partiesRefs : [];
            const adversas    = Array.isArray(ws?.partes?.adversas)
              ? ws.partes.adversas.map((p) => String(p?.nome || '').trim().split('(')[0].trim()).filter(Boolean)
              : [];
            const hasMetadata = cnj || juizo || comarca || classe || assunto || ramoDireito || adversas.length > 0 || partiesAll.length > 0;

            return (
              <div key={ws.id} className="border border-gray-300 rounded-lg bg-white overflow-hidden">
                {/* Cabeçalho do Caderno — nome real definido no master */}
                <div className="px-3 py-2 bg-[#20243B] flex items-center gap-2">
                  <FolderOpen className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                  <span className="text-xs font-bold text-white flex-1 truncate">{cadernoName}</span>
                </div>

                {/* Metadados estruturados (SSOT = painel master do Caderno de Autos) */}
                {hasMetadata && (
                  <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                    {partiesAll.length > 0 && partiesAll.map((p, i) => (
                      <React.Fragment key={`party-${i}`}>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">{String(p?.role || 'Parte').trim()}:</span>
                        <span className="text-gray-700">{toTitleCasePt(String(p?.name || '').trim())}</span>
                      </React.Fragment>
                    ))}
                    {adversas.length > 0 && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">Parte contrária:</span>
                        <span className="text-gray-700">{adversas.join(', ')}</span>
                      </>
                    )}
                    {comarca && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">Comarca:</span>
                        <span className="text-gray-700">{comarca}</span>
                      </>
                    )}
                    {juizo && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">Juízo:</span>
                        <span className="text-gray-700">{juizo}</span>
                      </>
                    )}
                    {cnj && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">CNJ:</span>
                        <span className="text-gray-700 font-mono">{cnj}</span>
                      </>
                    )}
                    {classe && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">Classe:</span>
                        <span className="text-gray-700">{classe}</span>
                      </>
                    )}
                    {assunto && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">Assunto:</span>
                        <span className="text-gray-700">{assunto}</span>
                      </>
                    )}
                    {ramoDireito && (
                      <>
                        <span className="font-semibold text-gray-800 whitespace-nowrap">Ramo do Direito:</span>
                        <span className="text-gray-700">{ramoDireito}</span>
                      </>
                    )}
                  </div>
                )}

                {/* ── Conteúdo do Caderno (Seções 1 e 2) ── */}
                <div className="px-3 py-3 space-y-5">
                  {/* ── SEÇÃO 1: DOCUMENTOS FORMAIS PARA ASSINATURA ── */}
                  {(() => {
                    const docsForWs = formalDocs
                      .filter((d) => d.clientVisible !== false)
                      .filter((d) =>
                        d.cadernoId === ws.id ||
                        (!d.cadernoId && formalDocs.filter((x) =>
                          String(x?.type || '').trim().toUpperCase() === String(d?.type || '').trim().toUpperCase()
                        ).length === 1)
                      )
                      .sort((a, b) => {
                        const order = { CONTRACT: 0, PROCURATION: 1, DECLARATION: 2, PETITION: 3, INFORMED_CONSENT: 4 };
                        return (order[String(a?.type || '').toUpperCase()] ?? 9) - (order[String(b?.type || '').toUpperCase()] ?? 9);
                      });
                    if (docsForWs.length === 0) return null;
                    return (
                      <div className="border border-gray-300 rounded-xl overflow-hidden shadow-md">
                        <div className="bg-[#20243B] px-4 py-2.5 flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#C5A059] text-white flex items-center justify-center text-[10px] font-bold shrink-0">1</div>
                          <div>
                            <h3 className="text-[11px] font-bold uppercase tracking-widest text-white">
                              {docsForWs.some(d => ['PETITION','INFORMED_CONSENT'].includes(String(d?.type||'').toUpperCase()))
                                ? 'Documentos, Petições e Consentimentos'
                                : 'Documentos Formais para Assinatura'}
                            </h3>
                            <p className="text-[10px] text-gray-300 mt-0.5">
                              {docsForWs.some(d => ['PETITION','INFORMED_CONSENT'].includes(String(d?.type||'').toUpperCase()))
                                ? 'Revise os documentos e petições abaixo. Quando necessário, aceite o consentimento informado.'
                                : 'Faça o download, assine e nos envie a foto ou PDF destes documentos.'}
                            </p>
                          </div>
                        </div>
                        <div className="bg-white px-3 py-3 space-y-2">
                          {docsForWs.map((docItem) => {
                            const docType = String(docItem?.type || '').trim().toUpperCase();
                            const isSigned = String(docItem?.status || '').trim().toUpperCase() === 'SIGNED';
                            const isPetitionType = docType === 'PETITION' || docType === 'INFORMED_CONSENT';
                            const consentStatus = String(docItem?.consentStatus || '').trim().toLowerCase();
                            const isConsentAccepted = consentStatus === 'accepted' || consentStatus === 'signed';
                            const isConsentDeclined = consentStatus === 'declined';
                            const isConsentPending = docItem?.requiresConsent && !isConsentAccepted && !isConsentDeclined;
                            const labelMap = { CONTRACT: 'Contrato', PROCURATION: 'Procuração', DECLARATION: 'Declaração de Hipossuficiência', PETITION: 'Petição', INFORMED_CONSENT: 'Consentimento Informado' };
                            const rawTitle = String(docItem?.title || docItem?.name || '').trim();
                            const title = rawTitle || labelMap[docType] || 'Documento';
                            const fileUrl = String(docItem?.fileUrl || docItem?.url || docItem?.webViewLink || '').trim();
                            const signedUrl = String(docItem?.signedFileUrl || '').trim();
                            const openUrl = signedUrl || fileUrl;
                            const isUploading = uploadingDocId === docItem.id;
                            return (
                              <div key={docItem.id} className={`rounded-lg bg-white flex items-center gap-3 px-3 py-2.5 border ${
                                isSigned || isConsentAccepted ? 'border-emerald-400 bg-emerald-50/20'
                                : isConsentDeclined ? 'border-red-300 bg-red-50/20'
                                : isConsentPending ? 'border-amber-300 bg-amber-50/20'
                                : 'border-gray-300'
                              } shadow-sm`}>
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                  isSigned || isConsentAccepted ? 'bg-emerald-500'
                                  : isConsentDeclined ? 'bg-red-500'
                                  : isConsentPending ? 'bg-amber-400'
                                  : isSigned ? 'bg-emerald-500' : 'bg-red-400'
                                }`} />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[12px] font-bold text-[#1a1e30] truncate" title={title}>{title}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {/* Badge de consentimento para petições */}
                                  {isPetitionType && isConsentPending && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-[9px] font-bold text-amber-700">
                                      <Clock className="w-2.5 h-2.5" /> Aguardando
                                    </span>
                                  )}
                                  {isPetitionType && isConsentAccepted && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-[9px] font-bold text-emerald-700">
                                      <CheckCircle className="w-2.5 h-2.5" /> Aceito
                                    </span>
                                  )}
                                  {isPetitionType && isConsentDeclined && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-red-200 bg-red-50 text-[9px] font-bold text-red-700">
                                      Recusado
                                    </span>
                                  )}
                                  {/* Botão Abrir / Baixar — sempre que houver URL */}
                                  {isSigned && signedUrl ? (
                                    <a
                                      href={signedUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                                    >
                                      Ver Assinado
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  ) : fileUrl ? (
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-200 bg-white text-[10px] font-bold text-[#20243B] hover:bg-gray-50 hover:border-gray-300 transition-colors"
                                    >
                                      Baixar
                                      <Download className="w-3 h-3" />
                                    </a>
                                  ) : null}
                                  {/* Botão Enviar — apenas para docs que precisam assinatura (NÃO petições/consentimento) */}
                                  {!isSigned && !isPetitionType && (
                                    <>
                                      <input
                                        ref={(el) => { if (el) signedInputRefs.current[docItem.id] = el; }}
                                        type="file"
                                        accept="application/pdf,image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) { e.target.value = ''; return; }
                                          await handleUploadSigned(docItem, file, ws.id);
                                          e.target.value = '';
                                        }}
                                      />
                                      <button
                                        type="button"
                                        disabled={isUploading}
                                        onClick={() => signedInputRefs.current[docItem.id]?.click()}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border transition-colors text-[10px] font-bold ${
                                          isUploading
                                            ? 'border-yellow-300 bg-yellow-50 text-yellow-600 cursor-wait animate-pulse'
                                            : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300'
                                        }`}
                                      >
                                        {isUploading ? 'Enviando...' : 'Enviar'}
                                        {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── SEÇÃO 2: DOCUMENTOS PESSOAIS E PROVAS ── */}
                  <div className="border border-gray-300 rounded-xl overflow-hidden shadow-md">
                    <div className="bg-[#20243B] px-4 py-2.5 flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#C5A059] text-white flex items-center justify-center text-[10px] font-bold shrink-0">2</div>
                      <div>
                        <h3 className="text-[11px] font-bold uppercase tracking-widest text-white">Seus Documentos e Provas</h3>
                        <p className="text-[10px] text-gray-300 mt-0.5">Seu caso pode ter diferentes pedidos — abra cada gaveta e anexe os arquivos solicitados.</p>
                      </div>
                    </div>
                    <div className="bg-white px-3 py-3 space-y-3">
                      {/* ── Mini banner dourado: Petição + Consentimento vinculados ── */}
                      {(() => {
                        const petConsentDocs = formalDocs.filter((d) => {
                          const t = String(d?.type || '').toUpperCase();
                          return (t === 'PETITION' || t === 'INFORMED_CONSENT') && d.cadernoId === ws.id;
                        });
                        if (petConsentDocs.length === 0) return null;
                        return (
                          <div className="rounded-lg border border-[#C5A059]/40 bg-gradient-to-r from-amber-50/80 to-white overflow-hidden">
                            <div className="px-3 py-1.5 bg-[#C5A059]/10 border-b border-[#C5A059]/20 flex items-center gap-2">
                              <i className="fas fa-shield-alt text-[9px] text-[#C5A059]" />
                              <span className="text-[9px] font-bold uppercase tracking-wider text-[#20243B]">Petição e Consentimento Informado</span>
                            </div>
                            <div className="px-3 py-2 space-y-1.5">
                              {petConsentDocs.map((d) => {
                                const dtype = String(d?.type || '').toUpperCase();
                                const cStatus = String(d?.consentStatus || '').toLowerCase();
                                const isAccepted = cStatus === 'accepted' || cStatus === 'signed';
                                const isDeclined = cStatus === 'declined';
                                const isPending = d?.requiresConsent && !isAccepted && !isDeclined;
                                const title = String(d?.title || d?.name || (dtype === 'PETITION' ? 'Petição' : 'Consentimento Informado')).trim();
                                const url = String(d?.fileUrl || d?.url || '').trim();
                                return (
                                  <div key={d.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-md border ${isAccepted ? 'border-emerald-200 bg-emerald-50/50' : isDeclined ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                                    <i className={`fas ${dtype === 'PETITION' ? 'fa-gavel' : 'fa-shield-alt'} text-[10px] ${isAccepted ? 'text-emerald-600' : isDeclined ? 'text-red-500' : 'text-amber-600'}`} />
                                    <span className="flex-1 text-[11px] font-semibold text-[#1a1e30] truncate">{title}</span>
                                    {isPending && <span className="text-[9px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">Aguardando</span>}
                                    {isAccepted && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">Aceito</span>}
                                    {isDeclined && <span className="text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">Recusado</span>}
                                    {url && dtype === 'PETITION' && (
                                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-600 hover:underline font-medium whitespace-nowrap">Ver PDF ↗</a>
                                    )}
                                    {dtype === 'INFORMED_CONSENT' && isPending && (
                                      <a href={`/view-published?documentId=${d.id}`} className="text-[9px] text-[#C5A059] hover:underline font-bold whitespace-nowrap">Ler e Aceitar →</a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      {fid ? (
                        <ClientDriveView driveFolderId={fid} clientId={clientId} hideHeader skipFolderFilter />
                      ) : (
                        <div className="text-center py-6 text-xs text-gray-500 font-medium bg-gray-50 rounded-lg border border-dashed border-gray-300">
                          Nenhuma pasta de envio configurada para este caderno ainda.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      )}

      {/* ── Seção 3: Aprovações de Peças (oculta quando vazia) ──── */}
      {clientId && <PortalAprovacoes clientId={clientId} />}
    </div>
  );
}
